<template>
  <div>
    <SectionLoader class="loader" :status="isLoading" :opacity="0.7" />
    <div v-if="!!groups" class="filters">
      <v-btn
        v-for="(group, index) in groups"
        :key="`${index}_${group.value}`"
        :color="group.value === currentGroup.value ? 'primary' : '#E2E2E2'"
        rounded
        depressed
        class="filters__btn"
        @click="setGroup(group)"
        >{{ group.name }}</v-btn
      >
    </div>
    <ReviewsCategoriesTableSearchBox
      v-bind="searchBoxAttrs"
      @change="$emit('search', $event)"
    />
    <div class="table-wrapper">
      <ReviewsCategoriesTable
        v-bind="tableAttrs"
        @sort="$emit('sort', $event)"
        @page-change="$emit('page-change', $event)"
        @go-to-details="goToDetails"
        @go-to-list="goToList"
      >
        <template v-slot:customColumn="{ item, columnName }">
          <slot name="customColumn" :column-name="columnName" :item="item" />
        </template>
      </ReviewsCategoriesTable>
    </div>
  </div>
</template>

<script>
import { FORMAT } from "@/config/date-format";
import moment from "moment";
import ReviewsCategoriesTable from "./ReviewsCategoriesTable.vue";
import ReviewsCategoriesTableSearchBox from "./ReviewsCategoriesTableSearchBox.vue";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { mapGetters, mapActions } from "vuex";
import SectionLoader from "@/components/Loader/SectionLoader";
import facilitiesService from "@/services/facilities";
import facilitiesGroupsService from "@/services/facilitiesGroups";
import constants from "@/config/constants";

export default {
  name: "ReviewsCategoriesTableContainer",
  components: {
    ReviewsCategoriesTable,
    ReviewsCategoriesTableSearchBox,
    SectionLoader
  },
  props: {
    searchBoxAttrs: {
      type: Object,
      required: true
    },
    tableAttrs: {
      type: Object,
      required: true
    },
    groups: {
      type: Array
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentGroup: null
    };
  },
  computed: {
    ...mapGetters("reviewsCategoriesAnalytics", [
      "getFacilityParams",
      "getSelectedDate",
      "getComparisonDate",
      "isComparing"
    ])
  },
  created() {
    if (this.groups) {
      this.currentGroup = this.groups[0];
    }
  },
  methods: {
    ...mapActions("reviewsCategoriesAnalytics", [
      "setIsComparing",
      "setComparisonDate",
      "setSelectedDate",
      "setIsComparing",
      "setSelectedFacility",
      "clearSelectedFacility"
    ]),
    setGroup(filter) {
      this.currentGroup = filter;
      const eventActions = {
        city: "cities",
        facility: "all",
        facility_group: "location_groups"
      };
      this.$gtm.trackCustomEvent({
        event:
          GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_DETAILS_TABLE_CONFIG_CHANGE,
        grouping: eventActions[filter.value]
      });
      this.$emit("group-change", { group_by: filter.value });
    },
    async getLocalSelectedFacility(itemDetails) {
      const selectedFacility = {};

      selectedFacility.id =
        itemDetails.facility || itemDetails.facility_group || itemDetails.city;
      selectedFacility.params = itemDetails;
      if (itemDetails.facility) {
        const { data } = await facilitiesService.getFacilityData(
          itemDetails.facility
        );
        selectedFacility.tab = 3;
        selectedFacility.meta = {
          type: "facility",
          displayName: data.name,
          storeCode: data.store_code,
          address: data.formatted_address,
          quantity: 0
        };
      }
      if (itemDetails.facility_group) {
        selectedFacility.tab = 2;
        const {
          data: { results }
        } = await facilitiesGroupsService.getFacilitiesGroups({
          groups: [itemDetails.facility_group]
        });
        selectedFacility.meta = {
          type: "facility_group",
          displayName: results[0].name,
          quantity: results[0].facilities_count
        };
      }
      if (itemDetails.city) {
        selectedFacility.tab = 1;
        selectedFacility.meta = {
          type: "city",
          displayName: itemDetails.city,
          quantity: 0
        };
      }

      return selectedFacility;
    },
    async setFacilitySelectData(facility) {
      const queries = { ...this.$route.query };
      delete queries["categoryId"];
      delete queries["facility"];
      delete queries["facilityGmb"];
      delete queries["city"];
      delete queries["facility_group"];
      delete queries["withoutCategory"];
      // this.resetGridPaging();
      this.reviewsListData = null;
      this.tab = null;
      const { params, meta, id, tab } = facility;
      this.$router.replace({
        query: {
          ...queries,
          ...params,
          ...meta,

          facility_tab: tab
        }
      });
      if (!id) {
        this.clearSelectedFacility();
        this.facilitiesDialog = false;
      }
      this.setSelectedFacility(facility);
    },
    async goToDetails(facility) {
      if (this.$route.name === "reviews-category-details") {
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_DETAILS_TABLE_ACTION,
          action: "show_details"
        });
        const selectedFacility = await this.getLocalSelectedFacility(facility);
        this.setFacilitySelectData(selectedFacility);

        this.initValues();
        return;
      }
      this.$gtm.trackCustomEvent({
        event:
          GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_OVERVIEW_TABLE_ROW_ACTION,
        action: "show_details"
      });
      const queries = { ...this.$route.query };
      this.$router.push({
        name: "reviews-category-details",
        query: {
          ...queries,
          ...this.getFacilityParams,
          ...facility,
          ...(this.getSelectedDate && {
            selectedFrom: this.getSelectedDate.date_from,
            selectedTo: this.getSelectedDate.date_to
          }),
          ...(this.getComparisonDate && {
            comparisonFrom: this.getComparisonDate.date_from,
            comparisonTo: this.getComparisonDate.date_to
          })
        }
      });
    },

    goToList(params) {
      const isDetailsPage = this.$route.name === "reviews-category-details";
      this.$gtm.trackCustomEvent({
        event: isDetailsPage
          ? GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_DETAILS_TABLE_ACTION
          : GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_OVERVIEW_TABLE_ROW_ACTION,
        action: "show_reviews"
      });
      let selectedDate = this.getSelectedDate;
      if (this.$route.query.dateType === constants.fullPeriod) {
        selectedDate = {
          date_from: moment(constants.absoluteStartDate).format(FORMAT.DATE),
          date_to: moment(Date.now()).format(FORMAT.DATE)
        };
      }
      const routeData = this.$router.resolve({
        name: "reviews-list",
        query: {
          ...params,
          ...selectedDate
        }
      });

      window.open(routeData.href, "_blank");
    }
  }
};
</script>
<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  z-index: 0;
}
.loader {
  z-index: 3;
}
.filters {
  display: flex;
  justify-content: flex-end;
  gap: $spacing;
  margin-bottom: $spacing * 3;
  margin-top: $spacing;
  padding: 0 $spacing * 2;

  &__btn {
    text-transform: unset;
    letter-spacing: unset;
  }
}
</style>
